export const M10_UNIT_3 = {
  id: "m10-u3-interrupted-stories",
  title: "Unit 3: Interrupted Stories",
  topic: "Past Continuous vs Past Simple + Leisure Activities",
  iconType: "map",
  grammarImage: "/m10-u3-interrupted-stories.jpg",
  grammarText: "Unit focus: Past Continuous vs Past Simple — using interrupted actions and storytelling to describe holidays, free time, and unexpected events.",

  sets: [

    // ===================================================================
    // SET 1: LEISURE ACTIVITIES VOCABULARY (B1+)
    // ===================================================================
    {
      id: "m10-u3-set1",
      title: "Set 1: Free Time Activities",
      context: `*Sunday afternoon - Emma's travel blog*

Emma writes about her favourite free time activities.
"When I'm on holiday, I love going hiking and taking photos."
"My friends prefer going camping or just relaxing on the beach."

*Domingo por la tarde - Blog de viajes de Emma*

Emma escribe sobre sus actividades de tiempo libre favoritas.
"Cuando estoy de vacaciones, me encanta hacer senderismo y tomar fotos."
"Mis amigos prefieren ir de campamento o simplemente relajarse en la playa."`,

      questions: [
        {
          type: "match",
          q: "Match the leisure activity to its meaning:",
          pairs: [
            { left: "go hiking", right: "hacer senderismo" },
            { left: "go camping", right: "ir de campamento" },
            { left: "go sightseeing", right: "hacer turismo" }
          ],
          a: "completed",
          successTip: "Many leisure activities use GO + verb-ing! Go hiking, go camping, go swimming, go surfing. Pattern! 🎒"
        },

        {
          type: "mc",
          q: "Emma loves ___ photos on her holidays.",
          options: ["taking", "doing", "making"],
          a: "taking",
          tip: "Take photos = tomar/sacar fotos."
        },

        {
          type: "match",
          q: "Match more leisure activities:",
          pairs: [
            { left: "sunbathe", right: "ir a tomar el sol" },
            { left: "eat out", right: "comer fuera" },
            { left: "go for a walk", right: "salir a caminar" }
          ],
          a: "completed"
        },

        {
          type: "mc",
          q: "On Sundays, they like to ___ at a nice restaurant.",
          options: ["eat out", "eat outside", "go eating"],
          a: "eat out",
          tip: "Eat out = comer fuera (en restaurante).",
          successTip: "Eat out = dine at a restaurant. Eat in = eat at home. Simple but important distinction! 🍽️"
        },

        {
          type: "mc",
          q: "Emma visited all the monuments. She spent the day ___.",
          options: ["sightseeing", "hiking", "camping"],
          a: "sightseeing",
          tip: "Sightseeing = visiting tourist attractions."
        },

        {
          type: "input",
          q: "Emma's blog: 'I spent the afternoon ___ on the beach, enjoying the sun.'",
          a: "sunbathing",
          alts: ["Sunbathing","sun bathing","swimming","relaxing","lying"],
          tip: "Sunbathe → sunbathing (drop 'e' + -ing)."
        },

        {
          type: "match",
          q: "Match the remaining activities:",
          pairs: [
            { left: "visit a museum", right: "visitar un museo" },
            { left: "read a book", right: "leer un libro" },
            { left: "watch a film", right: "ver una película" }
          ],
          a: "completed"
        },

        {
          type: "mc",
          q: "It was raining so they decided to ___ at the hotel.",
          options: ["relax", "go camping", "go hiking"],
          a: "relax",
          tip: "Relax = relajarse (indoor option)."
        },

        {
          type: "mc",
          q: "They ___ a museum about ancient history.",
          options: ["visited", "went", "saw"],
          a: "visited",
          tip: "Visit a museum = visitar un museo.",
          successTip: "Visit (museums, cities, people). Go to (places). See (films, shows). Each has its verb! 🎭"
        },

        {
          type: "input",
          q: "Emma's plan: 'Tomorrow I want to go ___ in the mountains.'",
          a: "hiking",
          alts: ["Hiking","running","camping","sightseeing","swimming"],
          successTip: "Go + verb-ing = leisure activities! Go hiking, go surfing, go dancing. Learn this pattern! 🥾"
        }
      ]
    },

    // ===================================================================
    // SET 2: HOLIDAY ACTIVITIES IN PROGRESS (B1+)
    // ===================================================================
    {
      id: "m10-u3-set2",
      title: "Set 2: What Were You Doing?",
      context: `*Monday morning - Coffee with a friend*

Emma's friend asks about her last holiday.
"What were you doing last summer?"
"Well, I was travelling through Portugal for two weeks."
"We were exploring small villages and eating amazing food!"

*Lunes por la mañana - Café con una amiga*

La amiga de Emma pregunta sobre sus últimas vacaciones.
"¿Qué estabas haciendo el verano pasado?"
"Bueno, estaba viajando por Portugal durante dos semanas."
"¡Estábamos explorando pequeños pueblos y comiendo comida increíble!"`,

      questions: [
        {
          type: "mc",
          q: "Last summer, while Emma ___ through Portugal, she lost her passport.",
          options: ["was travelling", "travelled", "travels"],
          a: "was travelling",
          tip: "Extended activity over a period = Past Continuous."
        },

        {
          type: "match",
          q: "Match the holiday actions:",
          pairs: [
            { left: "exploring", right: "explorando" },
            { left: "relaxing", right: "relajándose" },
            { left: "sunbathing", right: "tomando el sol" }
          ],
          a: "completed"
        },

        {
          type: "mc",
          q: "What ___ you doing on your last holiday?",
          options: ["were", "was", "did"],
          a: "were",
          tip: "What were you doing? = Past Continuous question."
        },

        {
          type: "input",
          q: "Emma's memory: 'We were ___ the old streets of Lisbon all morning.'",
          a: "exploring",
          alts: ["Exploring","sightseeing","walking","hiking","visiting"],
          tip: "Explore → exploring (drop 'e')."
        },

        {
          type: "mc",
          q: "At the beach resort, everyone ___ and enjoying the sun.",
          options: ["was relaxing", "relaxed", "relax"],
          a: "was relaxing",
          tip: "Scene description = Past Continuous."
        },

        {
          type: "mc",
          q: "The children ___ in the pool all afternoon when it started to rain.",
          options: ["were swimming", "swam", "swim"],
          a: "were swimming",
          tip: "Extended activity in the past = was/were + -ing.",
          successTip: "Past Continuous for duration! 'All afternoon, all morning, for two hours' = clues for Past Continuous! ⏳"
        },

        {
          type: "order",
          q: "Traduce: 'Estábamos comiendo fuera cuando empezó a llover.'",
          words: ["We", "were", "eating", "out", "when", "it", "started", "to", "rain."],
          a: "We were eating out when it started to rain.",
          extra: ["ate", "start", "raining"]
        },

        {
          type: "mc",
          q: "Emma and her friends ___ photos when the sun set.",
          options: ["were taking", "taking", "take"],
          a: "were taking",
          tip: "Activity in progress at that moment."
        },

        {
          type: "input",
          q: "Holiday scene: 'I was ___ on the balcony and reading my book.'",
          a: "relaxing",
          alts: ["Relaxing","sitting","lying","sunbathing"],
          tip: "Relax → relaxing."
        },

        {
          type: "mc",
          q: "While they ___ , they discovered a hidden beach.",
          options: ["were hiking", "hiked", "hike"],
          a: "were hiking",
          successTip: "While + Past Continuous sets the background for a discovery. Story structure begins here! 🗺️"
        }
      ]
    },

    // ===================================================================
    // SET 3: WHEN = THE INTERRUPTION (B1+)
    // ===================================================================
    {
      id: "m10-u3-set3",
      title: "Set 3: When It All Changed",
      context: `*Monday evening - Dinner table stories*

Emma tells her most dramatic holiday story.
"I was swimming in the ocean when I saw a shark fin!"
"Everyone was laughing when suddenly the lights went out."
Her friends lean in, wanting to hear more.

*Lunes por la noche - Historias en la cena*

Emma cuenta su historia de vacaciones más dramática.
"¡Estaba nadando en el océano cuando vi una aleta de tiburón!"
"Todos estaban riendo cuando de repente las luces se apagaron."
Sus amigos se inclinan hacia adelante, queriendo escuchar más.`,

      questions: [
        {
          type: "match",
          q: "Match the story structure:",
          pairs: [
            { left: "I was swimming", right: "action in progress" },
            { left: "when", right: "the interruption arrives" },
            { left: "I saw a shark", right: "completed sudden event" }
          ],
          a: "completed",
          successTip: "The formula! [Past Continuous] + WHEN + [Past Simple]. Long action + short interruption! ⚡"
        },

        {
          type: "mc",
          q: "I was hiking ___ I twisted my ankle.",
          options: ["when", "while", "during"],
          a: "when",
          tip: "When + sudden interruption = Past Simple.",
          successTip: "WHEN introduces the interruption (Past Simple). WHILE introduces another background action (Past Continuous)! 🔑"
        },

        {
          type: "mc",
          q: "Emma was reading on the beach when her phone ___.",
          options: ["rang", "ringing", "rings"],
          a: "rang",
          tip: "Phone rang = sudden completed action."
        },

        {
          type: "input",
          q: "Emma's story: 'I was taking photos when a monkey ___ my bag.'",
          a: "grabbed",
          alts: ["Grabbed","took","stole","snatched","seized"],
          tip: "Grab → grabbed.",
          successTip: "Grab = agarrar, tomar de repente"
        },

        {
          type: "mc",
          q: "They were camping when a storm ___.",
          options: ["arrived", "was arriving", "arrives"],
          a: "arrived",
          tip: "Storm = sudden interruption = Past Simple."
        },

        {
          type: "order",
          q: "Traduce: 'Estaba explorando la ciudad cuando me perdí.'",
          words: ["I", "was", "exploring", "the", "city", "when", "I", "got", "lost."],
          a: "I was exploring the city when I got lost.",
          extra: ["lose", "were", "explored"]
        },

        {
          type: "mc",
          q: "We were eating out when the waiter ___ our order.",
          options: ["dropped", "was dropping", "drops"],
          a: "dropped",
          tip: "Dropping the order = sudden event."
        },

        {
          type: "mc",
          q: "She was sightseeing ___ she found a beautiful hidden square.",
          options: ["when", "while", "since"],
          a: "when",
          tip: "Discovery = sudden event = when + Past Simple."
        },

        {
          type: "input",
          q: "Complete: 'I was sunbathing when it suddenly ___ to rain.'",
          a: "started",
          alts: ["Started", "began", "Began"],
          tip: "Start/begin → started/began (Past Simple)."
        },

        {
          type: "mc",
          q: "He was going for a walk when he ___ his old school friend.",
          options: ["saw", "was seeing", "sees"],
          a: "saw",
          tip: "Saw = past simple of see (sudden encounter).",
          successTip: "See (past: saw) = involuntary! You don't choose to suddenly see something - it just happens! Perfect for stories! 👁️"
        }
      ]
    },

    // ===================================================================
    // SET 4: WHILE = TWO ACTIONS (B2-)
    // ===================================================================
    {
      id: "m10-u3-set4",
      title: "Set 4: While It Was Happening",
      context: `*Tuesday morning - Writing workshop*

Emma teaches her friends how to tell better stories.
"Use WHILE to connect two things happening at the same time."
"While I was hiking, my friend was taking photos."
"While she was swimming, I was reading."

*Martes por la mañana - Taller de escritura*

Emma enseña a sus amigos cómo contar mejores historias.
"Usa WHILE para conectar dos cosas que suceden al mismo tiempo."
"Mientras yo hacía senderismo, mi amigo sacaba fotos."
"Mientras ella nadaba, yo leía."`,

      questions: [
        {
          type: "match",
          q: "Match the WHILE patterns:",
          pairs: [
            { left: "While I was reading,", right: "my dog was barking." },
            { left: "While they were hiking,", right: "it started to rain." },
            { left: "While he was sleeping,", right: "his mother was cooking." }
          ],
          a: "completed",
          successTip: "WHILE connects: two simultaneous actions (both -ing) OR a background action interrupted (Past Simple)! 🔄"
        },

        {
          type: "mc",
          q: "___ Emma was exploring the market, her friend was waiting at the café.",
          options: ["While", "When", "During"],
          a: "While",
          tip: "Two simultaneous actions = While + both Past Continuous."
        },

        {
          type: "mc",
          q: "While I was going for a walk, I ___ something strange.",
          options: ["noticed", "was noticing", "notice"],
          a: "noticed",
          tip: "While (background) + Past Simple (sudden discovery)."
        },

        {
          type: "input",
          q: "Emma's workshop: 'While my friends were sunbathing, I ___ the old town.' (explorar)",
          a: "was exploring",
          alts: ["Was exploring"],
          tip: "Two simultaneous = both Past Continuous."
        },

        {
          type: "mc",
          q: "___ she was visiting the museum, her bag was stolen.",
          options: ["While", "When", "At"],
          a: "While",
          tip: "While = during the time she was visiting.",
          successTip: "While vs When! WHILE = during a longer action. WHEN = at the moment of interruption. Subtle but important! 🎯"
        },

        {
          type: "mc",
          q: "While we were camping, it ___ every night.",
          options: ["rained", "was raining", "rains"],
          a: "rained",
          tip: "Repeated completed event = Past Simple.",
          successTip: "Careful! While + Past Continuous sets background, but the repeated event = Past Simple. 'It rained every night' = repeated fact! 🌧️"
        },

        {
          type: "order",
          q: "Traduce: 'Mientras ella leía, él escuchaba música.'",
          words: ["While", "she", "was", "reading,", "he", "was", "listening", "to", "music."],
          a: "While she was reading, he was listening to music.",
          extra: ["read", "listen", "listened"]
        },

        {
          type: "mc",
          q: "While they were eating out, ___ started playing live music.",
          options: ["the band", "a band was", "band"],
          a: "the band",
          tip: "Past Simple for the new event that started.",
          successTip: "The band started playing = sudden new event (Past Simple) interrupting the meal (Past Continuous)! 🎸"
        },

        {
          type: "input",
          q: "Story sentence: 'While I was watching the sunset, a dolphin ___ out of the water.'",
          a: "jumped",
          alts: ["Jumped", "leapt", "Leaped","sprang","came","burst","popped"],
          tip: "Sudden single event = Past Simple.",
          successTip: "No uses 'Go out of para movimientos que salen desde el agua hacia arriba. Suena incorrecto"
        },

        {
          type: "mc",
          q: "While Emma was writing her blog, her battery ___ and the screen went black.",
          options: ["died", "was dying", "dies"],
          a: "died",
          successTip: "Battery died = sudden, complete event (Past Simple). Perfect story interruption! 📱💀"
        }
      ]
    },

    // ===================================================================
    // SET 5: STORYTELLING ADVERBS (B2-)
    // ===================================================================
    {
      id: "m10-u3-set5",
      title: "Set 5: Dramatic Storytelling",
      context: `*Tuesday evening - Travel blog post*

Emma uses dramatic adverbs in her stories.
"I was relaxing by the pool. Suddenly, the alarm went off."
"We were hiking quietly. Then, all of a sudden, a bear appeared."
These words make stories exciting!

*Martes por la noche - Post del blog de viajes*

Emma usa adverbios dramáticos en sus historias.
"Estaba relajándome junto a la piscina. De repente, sonó la alarma."
"Estábamos haciendo senderismo en silencio. Luego, de repente, apareció un oso."
¡Estas palabras hacen que las historias sean emocionantes!`,

      questions: [
        {
          type: "match",
          q: "Match the storytelling adverbs:",
          pairs: [
{ left: "suddenly", right: "de repente (neutral)" },
{ left: "all of a sudden", right: "de repente (más dramático)" },
{ left: "just then", right: "justo en ese momento (preciso)" }
          ],
          a: "completed",
          successTip: "Dramatic adverbs signal interruptions! Suddenly, all of a sudden, just then = the moment everything changed! 🎬"
        },

        {
          type: "mc",
          q: "I was swimming peacefully. ___, something touched my foot.",
          options: ["Suddenly", "While", "During"],
          a: "Suddenly",
          tip: "Suddenly = de repente (dramatic moment)."
        },

        {
          type: "mc",
          q: "We were sightseeing. _____, it started raining heavily.",
          options: ["All of a sudden", "Just when", "While"],
          a: "All of a sudden",
          tip: "All of a sudden = de golpe (more dramatic than 'suddenly').",
          successTip: "Intensity scale! Suddenly < All of a sudden < Out of nowhere. More dramatic = more surprising! 🌪️"
        },

        {
          type: "input",
          q: "Emma's blog: 'We were eating out. ___ ___ , the chef came out and started singing.' (justo en ese momento)",
          a: "Just then",
          alts: ["just then"],
          tip: "Just then = justo en ese momento."
        },

        {
          type: "mc",
          q: "She was taking photos of the sunset ___ her camera fell into the sea.",
          options: ["when suddenly", "while suddenly", "during suddenly"],
          a: "when suddenly",
          tip: "'when suddenly' = classic storytelling combo.",
          successTip: "'Was + -ing... when suddenly' = the most dramatic storytelling formula in English! Use it! ✍️"
        },

        {
          type: "mc",
          q: "We were hiking up the mountain. ___ of nowhere, a storm arrived.",
          options: ["Out", "All", "Just"],
          a: "Out",
          tip: "Out of nowhere = de la nada (most surprising)."
        },

        {
          type: "order",
          q: "Traduce: 'Estaba durmiendo tranquilamente. De repente, sonó el teléfono.'",
          words: ["I", "was", "sleeping", "quietly.", "Suddenly,", "the", "phone", "rang."],
          a: "I was sleeping quietly. Suddenly, the phone rang.",
          extra: ["slept", "all", "just"]
        },

        {
          type: "mc",
          q: "___ I was relaxing by the pool, someone jumped in and splashed everyone!",
          options: ["Just as", "During", "All"],
          a: "Just as",
          tip: "Just as = justo cuando (precise moment).",
          successTip: "'Just as' = at the exact moment. More precise than 'when'. 'Just as I sat down, the show started!' ⏱️"
        },

        {
          type: "mc",
          q: "We were watching the football match. ___ then, the lights went out.",
          options: ["Just", "All", "Out"],
          a: "Just",
          tip: "Just then = justo en ese momento."
        },

        {
          type: "input",
          q: "Dramatic story: 'I was going for a walk ___ ___ the ground started shaking.'",
          a: "when suddenly",
          alts: ["When suddenly", "when all of a sudden", "When all of a sudden","just then"],
          successTip: "You now have a full storytelling toolkit! Was + -ing + when suddenly / just then / all of a sudden. 🎤"
        }
      ]
    },

    // ===================================================================
    // SET 6: TELLING HOLIDAY STORIES (B2-)
    // ===================================================================
    {
      id: "m10-u3-set6",
      title: "Set 6: Holiday Disasters",
      context: `*Wednesday afternoon - Emma's funniest holiday*

"It was the most chaotic holiday of my life!"
Emma tells her friends about the holiday where everything went wrong.
"It started perfectly. We were relaxing on the beach when..."

*Miércoles por la tarde - Las vacaciones más graciosas de Emma*

"¡Fueron las vacaciones más caóticas de mi vida!"
Emma cuenta a sus amigos las vacaciones donde todo salió mal.
"Empezó perfecto. Estábamos relajándonos en la playa cuando..."`,

      questions: [
        {
          type: "mc",
          q: "We were relaxing on the beach ___ a jellyfish stung my brother.",
          options: ["when", "while", "during"],
          a: "when",
          tip: "Sudden sting = Past Simple after 'when'."
        },

        {
          type: "mc",
          q: "While my sister was sunbathing, a seagull ___ her sandwich.",
          options: ["stole", "was stealing", "steals"],
          a: "stole",
          tip: "Single completed event = Past Simple.",
          successTip: "Steal → stole (irregular Past Simple). Seagulls steal food = very common beach problem! 🐦‍⬛😅"
        },

        {
          type: "order",
          q: "Traduce: 'Estábamos visitando un museo cuando se fue la luz.'",
          words: ["We", "were", "visiting", "a", "museum", "when", "the", "lights", "went", "out."],
          a: "We were visiting a museum when the lights went out.",
          extra: ["visit", "light", "were"]
        },

        {
          type: "mc",
          q: "I was going for a walk when I ___ into a fountain.",
          options: ["fell", "was falling", "fall"],
          a: "fell",
          tip: "Fall → fell (irregular Past Simple).",
          successTip: "Fall → fell → fallen. Irregular! Common in travel stories (trips, falls, accidents)! 🤣"
        },

        {
          type: "mc",
          q: "Emma's disaster: 'We were eating out when the waiter accidentally ___ wine on my dress.'",
           options: ["spilled", "was spilling", "spills"],
          a: "spilled",
          tip: "Spill → spilled/spilt (Past Simple).",
          successTip: "Spilled or spilt? Both are correct! Spilled is more common in American English, spilt in British English. Either way, it's a messy story! 🍷👗"
        },

        {
          type: "mc",
          q: "While we were camping, a fox ___ into our tent.",
          options: ["crept", "was creeping", "creep"],
          a: "crept",
          tip: "Creep → crept (irregular Past Simple).",
          successTip: "Creep → crept! Irregular verb. 'A fox crept in' = sneaky, quiet movement. Animals creep, people sneak! 🦊"
        },

        {
          type: "mc",
          q: "She was taking a photo of the view when she ___ her phone.",
          options: ["dropped", "was dropping", "drops"],
          a: "dropped",
          tip: "Drop = sudden accident = Past Simple."
        },

        {
          type: "order",
          q: "Traduce: 'Estábamos haciendo senderismo cuando de repente empezó a nevar.'",
          words: ["We", "were", "hiking", "when", "it", "suddenly", "started", "snowing."],
          a: "We were hiking when it suddenly started snowing.",
          extra: ["snow", "sudden", "hiked"]
        },

        {
          type: "mc",
          q: "I was sightseeing ___ I realised I had the wrong map.",
          options: ["when", "while", "during"],
          a: "when",
          tip: "Realisation = sudden moment = Past Simple after 'when'."
        },

        {
          type: "mc",
          q: "While we were swimming, the lifeguard ___ his whistle to warn us.",
          options: ["blew", "was blowing", "blows"],
          a: "blew",
          tip: "Blow → blew (irregular Past Simple, sudden action).",
          successTip: "Blow → blew → blown. Three forms! 'The lifeguard blew his whistle' = instant command. All heads turn! 🏊"
        }
      ]
    },

    // ===================================================================
    // SET 7: UNEXPECTED EVENTS (B2)
    // ===================================================================
    {
      id: "m10-u3-set7",
      title: "Set 7: Something Went Wrong",
      context: `*Thursday morning - Travel podcast*

Emma records a podcast episode: "Holidays from Hell."
"I was checking in at the airport when I realised my passport had expired!"
"My friend was boarding the plane when her suitcase burst open."

*Jueves por la mañana - Podcast de viajes*

Emma graba un episodio del podcast: "Las peores vacaciones."
"¡Estaba haciendo el check-in en el aeropuerto cuando me di cuenta de que mi pasaporte había vencido!"
"Mi amiga estaba abordando el avión cuando su maleta se abrió."`,

      questions: [
        {
          type: "mc",
          q: "Emma was checking in ___ she noticed her passport was expired.",
          options: ["when", "while", "just"],
          a: "when",
          tip: "Sudden realisation = Past Simple after 'when'."
        },

        {
          type: "mc",
          q: "While she was packing her bags, she ___ her camera charger.",
          options: ["forgot", "was forgetting", "forgets"],
          a: "forgot",
          tip: "Forget → forgot (Past Simple).",
          successTip: "Forget → forgot → forgotten. The 3 forms! 'I forgot my charger' = that moment of realisation. 😩"
        },

        {
          type: "input",
          q: "Travel disaster: 'We were waiting at the station when our train was ___ .'",
          a: "cancelled",
          alts: ["delayed","announced","diverted","rescheduled"],
          tip: "Passive Past Simple = was cancelled."
        },

        {
          type: "mc",
          q: "He was sleeping on the train when a thief ___ his wallet.",
          options: ["took", "was taking", "takes"],
          a: "took",
          tip: "Take → took (Past Simple, completed event)."
        },

        {
          type: "mc",
          q: "While we were exploring the jungle, we ___ our way back to camp.",
          options: ["lost", "were losing", "lose"],
          a: "lost",
          tip: "Lose → lost (Past Simple, completed).",
          successTip: "Lose → lost! 'We lost our way' = nos perdimos. Very useful phrase for travel stories! 🗺️😅"
        },

        {
          type: "order",
          q: "Traduce: 'Estaba tomando el sol cuando me di cuenta de que tenía quemadura de sol.'",
          words: ["I", "was", "sunbathing", "when", "I", "realised", "I", "had", "sunburn."],
          a: "I was sunbathing when I realised I had sunburn.",
          extra: ["sunbathe", "realize", "burn"]
        },

        {
          type: "mc",
          q: "They were going for a walk on the cliff when the path ___.",
          options: ["collapsed", "was collapsing", "collapses"],
          a: "collapsed",
          tip: "Sudden dangerous event = Past Simple."
        },

        {
          type: "mc",
          q: "While Emma was eating at the market, someone ___ her guidebook.",
          options: ["grabbed", "was grabbing", "grabs"],
          a: "grabbed",
          tip: "Grab = single completed action = Past Simple."
        },

        {
          type: "input",
          q: "Emma's podcast: 'I was swimming when I ___ something strange under the water.'",
          a: "felt",
          alts: ["Felt", "noticed", "Noticed","saw","felt","noticed","heard","touched","spotted"],
          tip: "Feel → felt (Past Simple, physical sensation)."
        },

        {
          type: "mc",
          q: "She was visiting a temple when her shoe ___.",
          options: ["broke", "was breaking", "breaks"],
          a: "broke",
          tip: "Break → broke (Past Simple, sudden event).",
          successTip: "Break → broke → broken. Classic holiday mishap! 'Her shoe broke' vs 'She was breaking her shoe' - very different! 👠😂"
        }
      ]
    },

    // ===================================================================
    // SET 8: QUESTIONS ABOUT THE PAST (B2)
    // ===================================================================
    {
      id: "m10-u3-set8",
      title: "Set 8: Asking About the Story",
      context: `*Thursday evening - Friends react to Emma's stories*

Friends ask Emma questions about her adventures.
"What were you doing when it happened?"
"Where were you staying when the storm hit?"
Emma loves answering these questions.

*Jueves por la noche - Los amigos reaccionan a las historias de Emma*

Los amigos hacen preguntas a Emma sobre sus aventuras.
"¿Qué estabas haciendo cuando ocurrió?"
"¿Dónde te estabas quedando cuando llegó la tormenta?"
Emma adora responder estas preguntas.`,

      questions: [
        {
          type: "match",
          q: "Match the question to its answer:",
          pairs: [
            { left: "What were you doing?", right: "I was hiking up the mountain." },
            { left: "Where were you staying?", right: "We were staying at a small hostel." },
            { left: "Who were you with?", right: "I was travelling with my sister." }
          ],
          a: "completed",
          successTip: "WH- questions in Past Continuous! What/Where/Who/Why + were + subject + -ing? Natural conversation! 💬"
        },

        {
          type: "mc",
          q: "___ were you doing when the accident happened?",
          options: ["What", "Where", "Who"],
          a: "What",
          tip: "What = asking about the action."
        },

        {
          type: "mc",
          q: "___ were you staying when the storm arrived?",
          options: ["Where", "What", "Who"],
          a: "Where",
          tip: "Where = asking about the location."
        },

        {
          type: "input",
          q: "Emma's friend asks: '___ were you travelling with when you got lost?'",
          a: "Who",
          alts: ["who"],
          tip: "Who = asking about the person."
        },

        {
          type: "mc",
          q: "___ were you going when you missed the bus?",
          options: ["Where", "What", "Why"],
          a: "Where",
          tip: "Going somewhere = Where."
        },

        {
          type: "mc",
          q: "___ were you feeling when you saw the shark?",
          options: ["How", "What", "Where"],
          a: "How",
          tip: "How = asking about feeling/state.",
          successTip: "How were you feeling? = ¿Cómo te sentías? Asking about emotional state in the past! Empathy language! 💙"
        },

        {
          type: "order",
          q: "Traduce: '¿Qué estabas haciendo cuando sonó la alarma?'",
          words: ["What", "were", "you", "doing", "when", "the", "alarm", "went", "off?"],
          a: "What were you doing when the alarm went off?",
          extra: ["did", "do", "rang"]
        },

        {
          type: "mc",
          q: "Emma: 'I was hiking.' Friend: '___ you alone when it happened?'",
          options: ["Were", "Was", "Did"],
          a: "Were",
          tip: "Yes/No question: Were + you + -ing?"
        },

        {
          type: "mc",
          q: "Friend: '___ you enjoying your holiday before the storm?'",
          options: ["Were", "Was", "Did"],
          a: "Were",
          tip: "You = were in Past Continuous."
        },

        {
          type: "input",
          q: "Emma asks back: '___ was your worst travel disaster?' → 'What ___ you doing when it happened?'",
          a: "what / were",
          alts: ["Were"],
          successTip: "Asking others = connecting! 'What were YOU doing when it happened?' Turn stories into conversations! 🗣️"
        }
      ]
    },
    // ===================================================================
    // SET 9: EXTENDED NARRATIVES (B2+)
    // ===================================================================
    {
      id: "m10-u3-set9",
      title: "Set 9: Writing the Full Story",
      context: `*Friday morning - Emma's travel blog entry*

Emma writes her most complete holiday story yet.
She uses multiple interruptions, descriptions, and dramatic adverbs.
"It was supposed to be a relaxing holiday, but nothing went as planned."

*Viernes por la mañana - Entrada del blog de viajes de Emma*

Emma escribe su historia de vacaciones más completa hasta ahora.
Usa múltiples interrupciones, descripciones y adverbios dramáticos.
"Se suponía que iban a ser unas vacaciones tranquilas, pero nada salió como planeado."`,

      questions: [
        {
          type: "mc",
          q: "Blog: 'We ___ breakfast when the hotel fire alarm ___.'",
          options: ["were having / went off", "had / was going off", "were having / was going off"],
          a: "were having / went off",
          tip: "Background (Continuous) + interruption (Simple)."
        },

        {
          type: "mc",
          q: "'While I ___ my bags, my taxi ___ without me.'",
          options: ["was packing / left", "packed / was leaving", "was packing / was leaving"],
          a: "was packing / left",
          tip: "Packing = in progress. Taxi left = completed event.",
          successTip: "'The taxi left without me' = abandonment complete! Past Simple for final, completed actions! 🚕💨"
        },

        {
          type: "order",
          q: "Traduce: 'Estaba durmiendo cuando alguien tocó la puerta. Era mi vecino de habitación.'",
          words: ["I", "was", "sleeping", "when", "someone", "knocked", "on", "the", "door.", "It", "was", "my", "roommate."],
          a: "I was sleeping when someone knocked on the door. It was my roommate.",
          extra: ["slept", "knock", "was sleeping"]
        },

        {
          type: "mc",
          q: "'I was exploring the market. Suddenly, I ___ an amazing street performer.'",
          options: ["spotted", "was spotting", "spots"],
          a: "spotted",
          tip: "Sudden discovery after 'Suddenly' = Past Simple.",
          successTip: "Spot = notice suddenly! 'I spotted' = lo distinguí de repente. More specific and vivid than 'I saw'! 👀"
        },

        {
          type: "mc",
          q: "Blog: 'While we ___ for the bus, a local woman ___ us to her house for coffee.'",
          options: ["were waiting / invited", "waited / was inviting", "were waiting / was inviting"],
          a: "were waiting / invited",
          tip: "Waiting = background. Invitation = single complete event."
        },

        {
          type: "input",
          q: "Emma's conclusion: 'Even though everything ___ wrong, it was the best holiday of my life.'",
          a: "went",
          alts: ["Went","got","turned"],
          tip: "Go → went (Past Simple of go)."
        },

        {
          type: "mc",
          q: "'Just as we ___ to leave, the sun ___ out from the clouds.'",
          options: ["were about / came", "were about / was coming", "about / came"],
          a: "were about / came",
          tip: "'Were about to leave' = estaban por irse (imminent action).",
          successTip: "'Was/were about to' = going to do something at that exact moment! 'I was about to leave when...' = Estaba a punto de... 🎯"
        },

        {
          type: "order",
          q: "Traduce: 'Estábamos a punto de irnos cuando el guía anunció una sorpresa.'",
          words: ["We", "were", "about", "to", "leave", "when", "the", "guide", "announced", "a", "surprise."],
          a: "We were about to leave when the guide announced a surprise.",
          extra: ["go", "said", "went"]
        },

        {
          type: "mc",
          q: "'All of a sudden, while we ___ the sunset, fireworks ___.'",
          options: ["were watching / started", "watched / were starting", "were watching / were starting"],
          a: "were watching / started",
          tip: "Watching = ongoing background. Fireworks started = completed event."
        },

        {
          type: "mc",
          q: "'Out of nowhere, a dolphin ___ while we ___ in the bay.'",
          options: ["appeared / were swimming", "was appearing / swam", "appeared / swam"],
          a: "appeared / were swimming",
          tip: "Appeared = sudden single event. Swimming = ongoing activity.",
          successTip: "You now write like a travel blogger! Background + sudden events + dramatic adverbs = compelling stories! ✍️🌍"
        }
      ]
    },

    // ===================================================================
    // SET 10: FULL CONTRAST - PAST SIMPLE VS PAST CONTINUOUS (B2+)
    // ===================================================================
    {
      id: "m10-u3-set10",
      title: "Set 10: The Complete Picture",
      context: `*Friday evening - Best holiday story competition*

Emma and her friends compete for the best story.
Each one must use Past Simple and Past Continuous perfectly.
"The one with the most dramatic story wins dinner!"

*Viernes por la noche - Competencia de mejor historia de vacaciones*

Emma y sus amigos compiten por la mejor historia.
Cada uno debe usar el Pasado Simple y el Continuo perfectamente.
"¡El que tenga la historia más dramática gana la cena!"`,

      questions: [
        {
          type: "match",
          q: "Match each use to its tense:",
          pairs: [
      // Un solo left con dos usos combinados en el right
{ left: "Past Continuous", right: "Background / scene setting" },
{ left: "Past Simple", right: "Interruptions + completed sequences" },
{ left: "Past Continuous + Past Simple", right: "Together = interrupted story" }
          ],
          a: "completed",
          successTip: "The master key! Past Continuous = frame/background. Past Simple = events/actions inside the frame! 🖼️"
        },

        {
          type: "mc",
          q: "'It ___ a beautiful evening. The sun ___ and birds ___ .'",
          options: ["was / was setting / were singing", "was / set / sang", "were / was setting / were singing"],
          a: "was / was setting / were singing",
          tip: "Scene description = Past Continuous for all background actions.",
          successTip: "Opening a story! 'It was...' + multiple Past Continuous actions = classic literary scene-setting! 📖"
        },

        {
          type: "mc",
          q: "'Then, suddenly, my phone ___. I ___ it and couldn't believe the news.'",
          options: ["rang / answered", "was ringing / was answering", "rang / was answering"],
          a: "rang / answered",
          tip: "Series of quick completed events = Past Simple.",
          successTip: "Quick sequence of events = all Past Simple! Rang, answered, heard, said, ran. Fast-paced action! ⚡"
        },

        {
          type: "order",
          q: "Traduce: 'Estaba sentada en la playa cuando de repente el cielo se oscureció.'",
          words: ["I", "was", "sitting", "on", "the", "beach", "when", "suddenly", "the", "sky", "went", "dark."],
          a: "I was sitting on the beach when suddenly the sky went dark.",
          extra: ["sat", "sit", "goes"]
        },

        {
          type: "mc",
          q: "'While everyone ___ dinner, the chef ___ out and ___ us a free dessert.'",
          options: ["was having / came / gave", "had / was coming / gave", "was having / came / was giving"],
          a: "was having / came / gave",
          tip: "Dinner = ongoing. Chef came + gave = two completed events."
        },

        {
          type: "mc",
          q: "Which sentence is correct for storytelling?",
          options: [
            "I was walking to the market when I saw my ex.",
            "I walked to the market when I was seeing my ex.",
            "I was walking to the market when I was seeing my ex."
          ],
          a: "I was walking to the market when I saw my ex.",
          tip: "Background (Continuous) + surprise event (Simple).",
          successTip: "Classic structure! Long action (was walking) + short surprise (saw). Native speaker storytelling pattern! 🎯"
        },

        {
          type: "input",
          q: "Emma's winning story: 'I ___ ___ for a walk on the beach when I found a bottle with a message inside.'",
          a: "was going",
          alts: ["Was going"],
          tip: "Walk = background activity = Past Continuous."
        },

        {
          type: "mc",
          q: "'The message ___ from a sailor who ___ the bottle into the sea fifty years ago.'",
          options: ["was / is throwing", "was / threw", "is / is throwed"],
          a: "was / threw",
          tip: "Message = description (was). Threw = completed past action.",
          successTip: "Throw → threw → thrown! Another useful irregular verb. 'He threw the bottle' = lo lanzó. Perfect for stories! 🍾"
        },

        {
          type: "order",
          q: "Traduce: 'Mientras leía el mensaje, mis manos temblaban de emoción.'",
          words: ["While", "I", "was", "reading", "the", "message,", "my", "hands", "were", "shaking", "with", "excitement."],
          a: "While I was reading the message, my hands were shaking with excitement.",
          extra: ["read", "shake", "shook"]
        },

        {
          type: "mc",
          q: "'I ___ the sailor's family, showed them the bottle, and we all ___.'",
          options: ["found / cried", "was finding / cried", "found / were crying"],
          a: "found / cried",
          tip: "Series of completed events = all Past Simple.",
          successTip: "🏆 UNIT 3 COMPLETE! Past Simple vs Continuous MASTERED! You can now tell dramatic stories like a native! 🌍✨ Unit 4 next!"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET (B2)
  // ===================================================================
  readingSet: {
    id: "m10-u3-reading",
    title: "Reading: The Perfect Holiday That Wasn't",
    category: "reading",

    questions: [
      {
        type: "passage",
        passage: `**Emma's Travel Blog - The Perfect Holiday That Wasn't**

Last summer, my friend Lucia and I decided to go camping in the mountains of northern Spain. We had planned everything perfectly — or so we thought.

On the first day, everything was going beautifully. We were hiking along a scenic trail, the sun was shining, and birds were singing in the trees around us. I was taking photos of the incredible views when suddenly I realised my phone had no signal. I didn't worry. We had a map.

Then, just as we were setting up our tent, a storm arrived out of nowhere. We were trying to fix the tent poles when the wind knocked the whole thing down. Lucia was laughing so hard she couldn't help. While she was wiping tears from her eyes, I was desperately trying to save our sleeping bags from the mud.`
      },

      {
        type: "mc",
        q: "Where did Emma and Lucia go camping?",
        options: ["Mountains of northern Spain", "Beach in Portugal", "Forest in France"],
        a: "Mountains of northern Spain",
        tip: "Check the opening sentence."
      },

      {
        type: "mc",
        q: "What was Emma doing when she realised her phone had no signal?",
        options: ["Taking photos", "Setting up the tent", "Hiking"],
        a: "Taking photos",
        tip: "'I was taking photos... when suddenly I realised...'"
      },

      {
        type: "mc",
        q: "What happened just as they were setting up the tent?",
        options: ["A storm arrived", "It started snowing", "They lost their map"],
        a: "A storm arrived",
        tip: "'Just as we were setting up our tent, a storm arrived.'"
      },

      {
        type: "mc",
        q: "What was Lucia doing while Emma was saving the sleeping bags?",
        options: ["Laughing and wiping tears", "Fixing the tent poles", "Looking at the map"],
        a: "Laughing and wiping tears",
        tip: "'While she was wiping tears...'"
      },

      {
        type: "passage",
        passage: `**Emma's Travel Blog - Continued**

The next morning, we woke up to brilliant sunshine. While we were eating breakfast outside our (now fixed) tent, a fox appeared from the trees and grabbed Lucia's bread roll right out of her hand. She was so shocked she just stared at it as it ran away.

That afternoon, we were going for a walk along the riverbank when we spotted an old stone bridge covered in flowers. We were admiring it and taking photos when a local farmer walked over to us.

"That bridge is five hundred years old," he said proudly. He invited us for coffee at his farmhouse, and we spent the whole evening listening to his stories about the village. It was the most unexpected and wonderful part of the trip.

Sometimes the best travel stories come from the things that go wrong.`
      },

      {
        type: "mc",
        q: "What did the fox do?",
        options: ["Grabbed Lucia's bread roll", "Scared Emma away", "Ate their breakfast"],
        a: "Grabbed Lucia's bread roll",
        tip: "'A fox appeared... and grabbed Lucia's bread roll.'"
      },

      {
        type: "mc",
        q: "What were they doing when they spotted the old bridge?",
        options: ["Going for a walk along the riverbank", "Eating breakfast", "Setting up the tent"],
        a: "Going for a walk along the riverbank",
        tip: "'We were going for a walk... when we spotted an old bridge.'"
      },

      {
        type: "mc",
        q: "How old was the bridge according to the farmer?",
        options: ["Five hundred years old", "One hundred years old", "A thousand years old"],
        a: "Five hundred years old",
        tip: "The farmer told them."
      },

      {
        type: "mc",
        q: "What did they do at the farmhouse?",
        options: ["Listened to stories about the village", "Ate dinner with the family", "Slept overnight"],
        a: "Listened to stories about the village",
        tip: "'We spent the whole evening listening to his stories.'"
      },

      {
        type: "mc",
        q: "What is the main message of Emma's blog post?",
        options: [
          "The best stories come from unexpected things going wrong",
          "Camping is always dangerous",
          "Spain is the best country for holidays"
        ],
        a: "The best stories come from unexpected things going wrong",
        tip: "Check the final line.",
        successTip: "Perfect reading! You followed a full story using Past Continuous and Past Simple naturally. Real English fluency! 📖🌟"
      }
    ]
  },

  // ===================================================================
  // REVIEW SET (B2+)
  // ===================================================================
  reviewSet: {
    id: "m10-u3-review",
    title: "Unit 3 Review: Stories & Interruptions",
    category: "review",

    questions: [
      {
        type: "mc",
        q: "Review: I was hiking when I ___ a beautiful waterfall.",
        options: ["discovered", "was discovering", "discover"],
        a: "discovered",
        tip: "Sudden discovery = Past Simple."
      },

      {
        type: "mc",
        q: "Review: While we ___ on the beach, a storm arrived.",
        options: ["were relaxing", "relaxed", "relax"],
        a: "were relaxing",
        tip: "Background action = Past Continuous."
      },

      {
        type: "mc",
        q: "Review: I was taking photos. ___, my camera battery died.",
        options: ["Suddenly", "While", "During"],
        a: "Suddenly",
        tip: "Sudden event = 'Suddenly' + Past Simple."
      },

      {
        type: "mc",
        q: "Review: She was ___ when she met her old friend.",
        options: ["sightseeing", "sightsaw", "sightsee"],
        a: "sightseeing",
        tip: "Past Continuous = sightseeing."
      },

      {
        type: "mc",
        q: "Review: ___ were you doing when the accident happened?",
        options: ["What", "Where", "Who"],
        a: "What",
        tip: "Asking about the action = What."
      },

      {
        type: "mc",
        q: "Review: We were about ___ leave when it started raining.",
        options: ["to", "of", "for"],
        a: "to",
        tip: "'Were about to' = estaban a punto de."
      },

      {
        type: "mc",
        q: "Review: Just ___ we were leaving, the host brought out more food.",
        options: ["as", "then", "when"],
        a: "as",
        tip: "'Just as' = at the exact moment."
      },

      {
        type: "mc",
        q: "Review: While they ___ dinner, someone knocked at the door.",
        options: ["were having", "had", "have"],
        a: "were having",
        tip: "Dinner = ongoing background = Past Continuous."
      },

      {
        type: "mc",
        q: "Review: He ___ , packed his bags and left without saying goodbye.",
        options: ["woke up", "was waking up", "wakes up"],
        a: "woke up",
        tip: "Series of quick actions = all Past Simple."
      },

      {
        type: "mc",
        q: "Review: All of ___ sudden, the lights went out.",
        options: ["a", "the", "an"],
        a: "a",
        tip: "'All of a sudden' = de golpe.",
        successTip: "🎉 UNIT 3 MASTERED! Past Simple vs Continuous ✅ Leisure vocab ✅ Storytelling adverbs ✅ Ready for Unit 4! 🚀"
      }
    ]
  }
};
